import { useEffect, useRef, useState } from 'react';
import './media.css';

// Video helpers shared by the product page and the Home page: a video can be
// a file (.mp4/.webm/.mov) or a YouTube link (watch, youtu.be, shorts, embed,
// live). YouTube is lighter for the site: the video streams from YouTube.

export function ytId(url?: string | null): string | null {
  return url?.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/))([\w-]{11})/)?.[1] ?? null;
}
export const isFileVideo = (url?: string | null) => !!url && /\.(mp4|webm|mov)(\?|$)/i.test(url);
export const isVideo = (url?: string | null) => isFileVideo(url) || !!ytId(url);
export const ytThumb = (url?: string | null) => {
  const id = ytId(url);
  return id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : '';
};
// Shorts are vertical; other YouTube videos are 16:9.
const ytRatio = (url: string) => (/\/shorts\//.test(url) ? 9 / 16 : 16 / 9);

/**
 * A YouTube video as a silent, looping background that covers its box (like
 * a muted <video> with object-fit: cover). It loads only once the box is
 * near the screen and fades in after YouTube's own title has gone.
 */
export function YtBg({ url, className, poster }: { url: string; className?: string; poster?: string }) {
  const id = ytId(url);
  const box = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [near, setNear] = useState(false);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const r = ytRatio(url);
    const fit = () => {
      const { width, height } = el.getBoundingClientRect();
      // a little larger than cover, to keep YouTube's edges and title out of view
      const w = Math.max(width, height * r) * 1.18;
      setSize({ w, h: w / r });
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    const io = new IntersectionObserver(([e]) => e.isIntersecting && setNear(true), { rootMargin: '300px' });
    io.observe(el);
    return () => {
      ro.disconnect();
      io.disconnect();
    };
  }, [url]);
  if (!id) return null;
  const q = `autoplay=1&mute=1&loop=1&playlist=${id}&controls=0&playsinline=1&modestbranding=1&rel=0&iv_load_policy=3&cc_load_policy=3&cc_lang_pref=zz&disablekb=1&fs=0`;
  return (
    <div ref={box} className={`ytbg${on ? ' on' : ''}${className ? ' ' + className : ''}`} aria-hidden="true">
      <img className="ytbg-p" src={poster || ytThumb(url)} alt="" />
      {near && size.w > 0 && (
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${id}?${q}`}
          title="Video"
          allow="autoplay; encrypted-media"
          tabIndex={-1}
          style={{ width: size.w, height: size.h }}
          // YouTube shows its title and play buttons for the first seconds; the poster stays until they are gone.
          onLoad={() => window.setTimeout(() => setOn(true), 4000)}
        />
      )}
    </div>
  );
}

/** A YouTube player with sound and controls, for video dialogs. */
export function YtPlayer({ url, start = 0, className }: { url: string; start?: number; className?: string }) {
  const id = ytId(url);
  if (!id) return null;
  return (
    <iframe
      className={className}
      src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&start=${Math.floor(start)}&rel=0&modestbranding=1&playsinline=1`}
      title="Video"
      allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
      allowFullScreen
    />
  );
}
